import { Meta, Title } from "@solidjs/meta";
import { useLocation } from "@solidjs/router";
import { type Component, Show } from "solid-js";
import { getAppBaseUrl } from "~/lib/getAppBaseUrl";
import { getSocialCardImageUrl } from "~/lib/socialCard";

const TITLE = "Sign up for Chief · Turn conversations into outcomes";
const DESCRIPTION =
  "Create a free Chief account. Chief dispatches AI agents to act on your conversations — shipping code, generating reports, and building presentations from your calls. New accounts start with 10,000 credits.";

/**
 * Emits the sign-up page's crawlable content during SSR. Routes under (app)
 * render client-only (ClientSideSplashScreen gates children on onMount), so a
 * crawler fetching /auth/signup would otherwise receive only the boot splash.
 * This renders outside that splash, so its head tags and readable copy reach
 * the server HTML. robots.txt allows /auth/signup and the sitemap lists it.
 */
export const SignUpSSRMeta: Component = () => {
  const location = useLocation();
  const url = `${getAppBaseUrl()}/auth/signup`;
  const imageUrl = getSocialCardImageUrl(getAppBaseUrl());

  // Reactive so both the head tags and the readable copy follow the route:
  // (app).tsx stays mounted across client-side navigation, so a one-shot guard
  // would leave the hidden <h1> in the DOM — and this <Title> overriding the
  // default — on every app page after sign-up.
  return (
    <Show when={location.pathname === "/auth/signup"}>
      <Title>{TITLE}</Title>
      <Meta name="description" content={DESCRIPTION} />
      <Meta property="og:title" content="Sign up for Chief" />
      <Meta property="og:description" content={DESCRIPTION} />
      <Meta property="og:type" content="website" />
      <Meta property="og:url" content={url} />
      <Meta property="og:site_name" content="Chief" />
      <Meta property="og:image" content={imageUrl} />
      <Meta name="twitter:card" content="summary_large_image" />
      <Meta name="twitter:title" content="Sign up for Chief" />
      <Meta name="twitter:image" content={imageUrl} />
      <div class="sr-only">
        <h1>Sign up for Chief</h1>
        <p>
          Chief is an AI workspace that acts on your conversations. It dispatches agents to ship code, generate reports,
          and build presentations from your calls and meetings. Create a free account to get 10,000 credits and start
          turning conversations into outcomes.
        </p>
      </div>
    </Show>
  );
};
